import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Room } from '../../models/room.model';
import { roomService } from '../../service/room.service';
import RoomDetails from './roomAddEdit';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';


interface RoomTableProps {
  propertyId: number;
  proposalId: number;
  clientPropertyMappingId?: number;
  propertyName: string;
  onBack: () => void;
  rmcClientMappingId?: number;
  hideCreateButton?: boolean;
  readOnlyMode?: boolean;
  hideSelectionCheckbox?: boolean; // <-- Add this prop
}

const RoomTable: React.FC<RoomTableProps> = ({
  propertyId,
  propertyName,
  clientPropertyMappingId,
  proposalId,
  onBack,
  hideCreateButton,
  rmcClientMappingId,
  readOnlyMode,
  hideSelectionCheckbox = false // <-- Default to false
}) => {
  const [roomDetails, setRoomDetails] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showRoomDrawer, setShowRoomDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userRole = useSelector((s: any) => s.auth.userRole) ?? '';
  const rmcId = useSelector((s: any) => s.auth.rmcId) ?? '';

  // Sorting and pagination state
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem('roomTablePage');
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem('roomTableRows');
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [sortField, setSortField] = useState(() => {
    const savedSort = localStorage.getItem('roomTableSortField');
    return savedSort || "name";
  });
  const [sortOrder, setSortOrder] = useState<1 | -1>(() => {
    const savedOrder = localStorage.getItem('roomTableSortOrder');
    return savedOrder ? parseInt(savedOrder) as 1 | -1 : 1;
  });

  const navigate = useNavigate();



  const fetchRoomDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await roomService.getByPropertyId(propertyId);
      console.log('Room details result:', res);
      
      // Sort the data
      const sortedData = [...(res || [])].sort((a, b) => {
        const aValue = a[sortField as keyof Room];
        const bValue = b[sortField as keyof Room];
        
        if (aValue === null) return sortOrder;
        if (bValue === null) return -sortOrder;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder * aValue.localeCompare(bValue);
        }
        
        return sortOrder * (Number(aValue) - Number(bValue));
      });

      setRoomDetails(sortedData || []);
    } catch (error) {
      console.error('Error fetching room details:', error);
      setRoomDetails([]);
    } finally {
      setIsLoading(false);
    }
  }, [propertyId, sortField, sortOrder]);

  const handleDeleteRoom = async (roomId: number) => {
    await roomService.delete(roomId);
    fetchRoomDetails();
  };

  const handleEditRoom = (room: Room) => {
    setSelectedRoom(room);
    setShowRoomDrawer(true);
  };

  const handleCreateRoom = () => {
    setSelectedRoom(null);
    setShowRoomDrawer(true);
  };

  // Selection/mapping removed: no saveMappings

  useEffect(() => {
    fetchRoomDetails();
    console.log('Proposal', proposalId);
  }, [fetchRoomDetails]);

  // Refresh room list when the drawer closes (transition from open -> closed)
  const prevShowDrawerRef = useRef<boolean>(showRoomDrawer);
  useEffect(() => {
    if (prevShowDrawerRef.current && !showRoomDrawer) {
      // drawer transitioned from open to closed
      fetchRoomDetails();
    }
    prevShowDrawerRef.current = showRoomDrawer;
  }, [showRoomDrawer, fetchRoomDetails]);

  const actionsBody = useMemo(() => (room: Room) => (
    <>
      <Button
        variant="link"
        className="p-0 me-2"
        title={userRole === 'Administrator' ? 'Edit Room' : 'View Room'}
        onClick={() => handleEditRoom(room)}
      >
        <i
          className={`bi ${userRole === 'Administrator' ? 'bi-pencil' : 'bi-eye'}`}
          style={{ fontSize: '18px' }}
        ></i>
      </Button>
      {userRole === 'Administrator' && (
        <Button
          variant="link"
          className="p-0"
          onClick={() => handleDeleteRoom(room.id)}
        >
          <i className="bi bi-trash" />
        </Button>
      )}
    </>
  ), [userRole]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div></div> {/* Empty div for spacing */}
        <div>
          {!hideCreateButton && !readOnlyMode && (
            <Button
              variant="primary"
              size="sm"
              className="me-2"
              onClick={handleCreateRoom}
            >
              <i className="bi bi-plus-lg me-1" />
              Create Room
            </Button>
          )}
        </div>
      </div>

      <DataTable
        value={roomDetails}
        paginator
        rows={rows}
        first={(page - 1) * rows}
        onPage={(e) => {
          const newPage = (e.first ?? 0) / rows + 1;
          setPage(newPage);
          setRows(e.rows);
          localStorage.setItem('roomTablePage', newPage.toString());
          localStorage.setItem('roomTableRows', e.rows.toString());
        }}
        onSort={(e) => {
          const newSortField = e.sortField || "name";
          const newSortOrder = e.sortOrder as 1 | -1;
          setSortField(newSortField);
          setSortOrder(newSortOrder);
          localStorage.setItem('roomTableSortField', newSortField);
          localStorage.setItem('roomTableSortOrder', newSortOrder.toString());
        }}
        sortField={sortField}
        sortOrder={sortOrder}
        dataKey="id" 
        responsiveLayout="scroll"
        emptyMessage={roomDetails.length === 0 ? "No rooms found for this property. Click 'Create Room' to add rooms." : ""}
        loading={isLoading}
        rowsPerPageOptions={[5, 10, 20]}
        stripedRows
        sortMode="single"
      >
        {/* selection checkbox removed */}
        <Column field="name" header="Name" sortable />
        <Column field="description" header="Description" sortable />
        <Column field="categoryDescription" header="Category Description" sortable />
        <Column field="area" header="Area (sqft)" sortable />
        <Column field="numberOfBedrooms" header="Bedrooms" sortable />
        <Column field="numberOfBathrooms" header="Bathrooms" sortable />
        <Column field="totalInventory" header="Inventory" sortable />
        <Column
          header="Actions"
          body={actionsBody}
        />
      </DataTable>
      {/* actions */}
      <div className="d-flex justify-content-end mt-3">
        {/* Save Changes removed - selection/mapping not supported */}
        {/* <button className="btn btn-secondary" onClick={() => navigate(-2)}>
                      Go Back
                    </button> */}
      </div>
      <RoomDetails
        show={showRoomDrawer}
        onClose={() => setShowRoomDrawer(false)}
        propertyId={propertyId}
        propertyName={propertyName}
        roomId={selectedRoom?.id}
        rmcClientMappingId={rmcClientMappingId ?? 0}
        onRoomSaved={fetchRoomDetails}
        asDrawer={true}
        prefetchedRoom={selectedRoom}
        readOnlyMode={readOnlyMode}
      />

    </div>
  );
};

export default RoomTable;
